export interface AuthSession {
  accessToken: string;
  tokenType: "bearer";
  expiresAt: number;
}

export const AUTH_SESSION_STORAGE_KEY = "dc_auth_session_v1";
let cachedRawSession: string | null | undefined;
let cachedSession: AuthSession | null | undefined;

function canUseStorage(): boolean {
  return (
    typeof window !== "undefined" && typeof window.localStorage !== "undefined"
  );
}

export function isAuthSessionExpired(
  session: AuthSession,
  nowMs = Date.now(),
): boolean {
  return nowMs >= session.expiresAt;
}

export function loadAuthSession(): AuthSession | null {
  if (!canUseStorage()) return null;
  const raw = window.localStorage.getItem(AUTH_SESSION_STORAGE_KEY);
  if (raw === cachedRawSession && cachedSession !== undefined) {
    return cachedSession;
  }
  if (!raw) {
    cachedRawSession = raw;
    cachedSession = null;
    return null;
  }

  try {
    const parsed = JSON.parse(raw) as Partial<AuthSession> | null;
    if (
      !parsed ||
      typeof parsed.accessToken !== "string" ||
      parsed.accessToken.length === 0 ||
      parsed.tokenType !== "bearer" ||
      typeof parsed.expiresAt !== "number"
    ) {
      window.localStorage.removeItem(AUTH_SESSION_STORAGE_KEY);
      cachedRawSession = null;
      cachedSession = null;
      return null;
    }

    const session = {
      accessToken: parsed.accessToken,
      tokenType: parsed.tokenType,
      expiresAt: parsed.expiresAt,
    };
    cachedRawSession = raw;
    cachedSession = session;
    return session;
  } catch {
    window.localStorage.removeItem(AUTH_SESSION_STORAGE_KEY);
    cachedRawSession = null;
    cachedSession = null;
    return null;
  }
}

export function saveAuthSession(session: AuthSession): void {
  if (!canUseStorage()) return;
  const raw = JSON.stringify(session);
  window.localStorage.setItem(AUTH_SESSION_STORAGE_KEY, raw);
  cachedRawSession = raw;
  cachedSession = session;
}

export function clearAuthSession(): void {
  if (!canUseStorage()) return;
  window.localStorage.removeItem(AUTH_SESSION_STORAGE_KEY);
  cachedRawSession = null;
  cachedSession = null;
}
